import { productFactsOnly, isLegalDecisionKey, isProductQuestion, productDialogue } from './productFactDialogue';
export const RESEARCH_SUMMARY_MAX_CHARS = 400;
// obs_note_listed_: 류 주 제외 목록 확인(2026-09-25) — 사용자가 목록을 보고 직접 답한다. 웹검증·LLM 이 대신 제안하지 않는다.
export const requiresDirectProductAnswer = key => /^(cabinet_|juice_|chapter20_juice_|chapter20_brix_|obs_note_listed_)/.test(String(key || ''));



/**
 * 웹검증 요약에서 세번·분류 추정 문장을 뺀다. 요약에는 "가황고무는 제4015.90호, 부직포는 제6210호로 분류" 같은
 * LLM의 분류 추측이 섞이는데, 그 문장이 결정트리 문언이나 사실 제안에 들어가면 근거 없는 류가 깨어난다.
 * 제품이 무엇인지 설명하는 문장만 남긴다.
 */
const CLASSIFICATION_TALK = /(HS\s?\d|HS코드|HS 코드|세번|품목분류|관세율표|판정번호|관세법령|제\s?\d{4}(?:\.\d{2})?\s?호|\d{4}\.\d{2}|\d{4}\.\d{2}-\d{4}|류로|호로|분류됩니다|분류된다|분류될|해당됩니다|해당될|CLIP|unipass|https?:\/\/)/i;
export function stripClassificationTalk(text) {
  const raw = String(text || "").replace(/\s+/g, " ").trim();
  if (!raw) return "";
  const sentences = raw.split(/(?<=[.!?。]|다\.|니다\.|습니다\.)\s+|(?<=\.)\s+/).map((s) => s.trim()).filter(Boolean);
  const kept = sentences.filter((sentence) => !CLASSIFICATION_TALK.test(sentence));
  return kept.join(" ").trim();
}

// A description is a reviewable excerpt, not an inferred enum answer. Never cut a
// sentence at the character limit (a trailing negation could change its meaning).
export function researchDescriptionProposal(summary) {
  const value = stripClassificationTalk(summary);
  if (!value || value.length > 500) return null;
  return { fact_key: 'product_description', prompt_ko: '웹조사 상품 설명', value,
    source_span: value, source_kind: 'unverified_research_summary', status: 'proposed' };
}

/** 보조 결정례 검색어: 웹검증이 특정한 제품명의 한글 부분 등. 최대 3개, 각 80자. */
export function normalizeHintQueries(queries) {
  const seen = new Set();
  const out = [];
  (Array.isArray(queries) ? queries : []).forEach((value) => {
    const text = String(value || "").replace(/\s+/g, " ").trim().slice(0, 80);
    if (!text || seen.has(text.toLowerCase())) return;
    seen.add(text.toLowerCase());
    if (out.length < 3) out.push(text);
  });
  return out;
}

/** 웹검증 정체 문장에서 결정례 검색에 쓸 한글 구절("Infab … (방사선 차폐 방호복)" → "방사선 차폐 방호복"). */
export function hintQueriesFromResearch(research) {
  if (!research) return [];
  const korean = (text) => (String(text || "").match(/[가-힣][가-힣·\s]{1,40}[가-힣]/g) || [])
    .map((m) => m.replace(/\s+/g, " ").trim()).filter((m) => m.length >= 2);
  const fromIdentity = korean(research.identity);
  return normalizeHintQueries(fromIdentity.slice(0, 2));
}

export function buildSubdivisionPreviewItem(query, productDetails = {}, confirmedFacts = {}, researchSummary = "", hintQueries = [], candidateHs4s = [], signalHeading = null) {
  productDetails = productDetails && typeof productDetails === 'object' ? productDetails : {};
  const clean = (value) => String(value || "").trim();
  const material = clean(productDetails.material);
  const intendedUse = clean(productDetails.intendedUse);
  const construction = clean(productDetails.construction);
  const articleRole = clean(productDetails.articleRole);
  // 웹 요약은 사실 제안·사례 검색에만 쓴다. keyword도 규칙 파서가 읽으므로
  // 미확인 요약을 여기에 합치면 confirmedFacts 확인 게이트를 우회한다.
  const context = [
    material && `재질·구성: ${material}`,
    intendedUse && `용도·사용처: ${intendedUse}`,
    construction && `형태·작동방식: ${construction}`,
  ].filter(Boolean);
  const facts = {};
  if (material) facts.user_material_description = material;
  if (intendedUse) facts.user_intended_use = intendedUse;
  if (construction) facts.user_construction_description = construction;
  // 제84류의 초기 슬라이스는 item_role, 이후 제84·85·90류는 article_role을
  // 사용한다. 사용자가 직접 선택한 동일 값만 두 별칭에 전달해 충돌을 만들지 않는다.
  if (articleRole) {
    facts.item_role = articleRole;
    facts.article_role = articleRole;
    // 상품 모드는 article_role·item_role 을 법적 키로 거른다 — 서버의 관측 키(obs_article_role)로도 보낸다(2026-10-14 사용자 결정 A).
    // '완성품' 값은 트리마다 이름이 달라(complete_machine·complete_article) 공통인 부분품·부속품만 넘긴다. 완성품은 카드 질문에서 답한다.
    if (articleRole === "part" || articleRole === "accessory") facts.obs_article_role = articleRole;
  }
  // 사전 점검 카드에서 결정트리 질문에 직접 답한 사실. 트리가 낸 fact_key 그대로 전달하며
  // 사용자가 방금 답한 값이 이전 상품 정보보다 우선한다. item_role 계열은 두 별칭을 같이 맞춘다.
  Object.entries(productFactsOnly(confirmedFacts)).forEach(([key, value]) => {
    if (value === undefined || value === null || value === "") return;
    facts[key] = value;
    if (key === "item_role" || key === "article_role") {
      facts.item_role = value;
      facts.article_role = value;
    }
  });
  // 카드에서 방금 답한 완성품·부분품(옛 키 item_role·article_role)이 상품 정보와 다르면 상품 정보의 관측 값은 보내지 않는다.
  const answeredRole = confirmedFacts && (confirmedFacts.item_role ?? confirmedFacts.article_role);
  if (answeredRole && confirmedFacts.obs_article_role == null && answeredRole !== facts.obs_article_role) {
    if (answeredRole === "part" || answeredRole === "accessory") facts.obs_article_role = answeredRole;
    else delete facts.obs_article_role;
  }
  Object.keys(facts).forEach(key => { if (isLegalDecisionKey(key)) delete facts[key]; });
  facts._product_fact_dialogue = true;
  // 후보 호 힌트 — 검색어 어휘가 트리를 못 깨울 때 서버가 그 호를 맡는 트리를 연다. 사실이 아니며 서버가 평가기에 주기 전에 떼어 낸다.
  const hints = (Array.isArray(candidateHs4s) ? candidateHs4s : []).map((h) => (typeof h === "string" ? { hs4: h, source: "" } : { hs4: String(h?.hs4 || ""), source: String(h?.source || "") })).filter((h) => /^\d{4}$/.test(h.hs4)).slice(0, 8);
  if (hints.length) facts._candidate_hs4s = hints;
  // 신호 호(2026-09-25) — LLM 후보와 관세사 이력이 같은 호를 가리킬 때만(또는 사용자가 다른 후보로 바꿔 계산할 때). 사실이 아니며 그 호의 트리가 가정 계산에만 쓴다.
  if (signalHeading && /^\d{4}$/.test(String(signalHeading.hs4 || ""))) {
    facts._signal_heading = { hs4: String(signalHeading.hs4), hs6: String(signalHeading.hs6 || ""), basis: String(signalHeading.basis || ""),
      alternatives: (Array.isArray(signalHeading.alternatives) ? signalHeading.alternatives : []).map(String).slice(0, 6) };
    // 이미 이 호의 가정 결과가 보이는 상태에서 그 안의 답을 잇는다 — 서버는 사용자가 답한 뒤에는 이때만 신호로 연다(Codex 재대조 4).
    if (signalHeading.continued === true) facts._signal_heading.continued = true;
  }
  const precedentHintQueries = normalizeHintQueries(hintQueries);
  return {
    id: "main-search-subdivision-preview",
    keyword: [clean(query), ...context].filter(Boolean).join(" · "),
    ...(Object.keys(facts).length ? { facts } : {}),
    ...(precedentHintQueries.length ? { precedent_hint_queries: precedentHintQueries } : {}),
  };
}

/**
 * AI 웹검증 요약을 현재 결정트리의 열린 축에 투영해 '제안'을 받는다. 제안은 사용자가 확인하기 전에는
 * 트리에 들어가지 않으며, 백엔드는 원문 근거 문장이 있는 값만 돌려준다.
 */
export async function fetchResearchFactProposals({
  query, summary, identity, shadow, token, signal, fetchImpl = fetch,
}) {
  if (!token) return { proposals: [], reason: "auth_required" };
  const questions = [...productDialogue(shadow).questions, ...(productDialogue(shadow).deferred_questions || [])];
  const description = questions.some(q => q.fact_key === 'product_description') ? researchDescriptionProposal(summary) : null;
  const enumQuestions = questions.filter(q => !requiresDirectProductAnswer(q.fact_key || q.key) && Array.isArray(q.options) && q.options.length);
  if (!enumQuestions.length && description) return { proposals: [description], conflictingKeys: [], reason: 'description_confirmation_required' };
  const response = await fetchImpl("/api/onto/classify/preview/research-facts", {
    method: "POST",
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    },
    body: JSON.stringify({
      keyword: query,
      summary: summary || "",
      identity: identity || "",
      shadow: shadow && typeof shadow === "object" ? {
        questions: enumQuestions,
        editable_facts: (Array.isArray(shadow.editable_facts) ? shadow.editable_facts : []).filter(q => isProductQuestion(q) && !requiresDirectProductAnswer(q.fact_key || q.key)),
      } : null,
    }),
    signal,
  });
  if (!response.ok) throw new Error(`HTTP ${response.status}`);
  const payload = await response.json();
  const proposals = Array.isArray(payload?.proposed) ? payload.proposed
    .filter((p) => p && p.fact_key && !requiresDirectProductAnswer(p.fact_key) && isProductQuestion(p) && p.value !== undefined && p.value !== null && String(p.value) !== "")
    : [];
  const conflictingKeys = Array.isArray(payload?.conflicting_keys)
    ? payload.conflicting_keys.map((k) => String(k || "")).filter(Boolean) : [];
  return {
    proposals: description ? [...proposals.filter(p => p.fact_key !== 'product_description'), description] : proposals,
    conflictingKeys,
    reason: String(payload?.reason || (proposals.length ? "proposed" : "no_grounded_facts")),
  };
}

export async function fetchSubdivisionPreview({
  query, productDetails, confirmedFacts, researchSummary, hintQueries, candidateHs4s = [], signalHeading = null, legacyRow = null, token, signal, fetchImpl = fetch,
  withHints = true,
}) {
  if (!token) return { row: null, error: "auth_required" };
  const item = buildSubdivisionPreviewItem(query, productDetails, confirmedFacts, researchSummary, hintQueries, candidateHs4s, signalHeading);
  // 결정트리만 다시 계산(2026-09-27): 첫 계산의 기존 엔진 결과를 실어 보내면 서버가 LLM 이 든 기존 엔진을 다시 돌리지 않는다.
  // 서버는 검색어가 첫 계산과 같을 때만 재사용하고, 결과는 전체 계산과 같다(BulkClassifyServiceTest).
  const reuse = legacyRow && legacyRow.status === "ok" && typeof legacyRow.keyword === "string";
  if (reuse) item.legacy_row = { status: "ok", keyword: legacyRow.keyword, hsk: legacyRow.hsk ?? null, hs6: legacyRow.hs6 ?? null, hs4: legacyRow.hs4 ?? null,
    chapter: legacyRow.chapter ?? null, name_ko: legacyRow.name_ko ?? null, confidence: legacyRow.confidence ?? null, score: legacyRow.score ?? null,
    decision_label: legacyRow.decision_label ?? null, top_candidates: Array.isArray(legacyRow.top_candidates) ? legacyRow.top_candidates : [] };
  const response = await fetchImpl("/api/onto/classify/bulk", {
    method: "POST",
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    },
    body: JSON.stringify({
      items: [item],
      options: { subdivision_shadow: true, ...(withHints ? { precedent_hints: true } : {}), ...(reuse ? { tree_only: true } : {}) },
    }),
    signal,
  });
  if (!response.ok) throw new Error(`HTTP ${response.status}`);
  const payload = await response.json();
  const row = Array.isArray(payload?.results) ? payload.results[0] || null : null;
  // 카드가 답·후보로 다시 계산해도 PROVE 모델 제안(검색어로만 정해짐)은 이어 붙인다 — 재계산 응답에는 없다.
  const model = lastModelProposal.get(String(query || ""));
  if (row && model && row.precedent_hints && typeof row.precedent_hints === "object" && !row.precedent_hints.model_proposal) {
    row.precedent_hints = { ...row.precedent_hints, model_proposal: model };
  }
  return { row, error: null };
}

// 검색어별 마지막 PROVE 모델 제안(2026-09-29) — 힌트 요청이 받아 두고, 같은 검색어의 재계산 응답에 이어 붙인다. 최근 50개.
const lastModelProposal = new Map();
function rememberModelProposal(query, model) {
  const key = String(query || "");
  if (!key || !model) return;
  lastModelProposal.delete(key);
  lastModelProposal.set(key, model);
  if (lastModelProposal.size > 50) lastModelProposal.delete(lastModelProposal.keys().next().value);
}

/**
 * 웹 조사가 끝난 뒤 PROVE 모델 제안만 다시 받는다(2026-10-04). 결정례 힌트는 서버가 30분 기억하므로 같은 요청이 싸다.
 * 성공하면 그 제안을 검색어의 마지막 제안으로 기억해 재계산 응답에도 이어 붙인다. 실패·모델 꺼짐은 null.
 */
export async function fetchModelWithResearch({ query, researchSummary, productDetails = null, token, fetchImpl = fetch }) {
  if (!query || !researchSummary) return null;
  const hints = await fetchPrecedentHints({ query, token, fetchImpl, withModel: true, productDetails, researchSummary });
  const model = hints && hints.model_proposal && hints.model_proposal.research ? hints.model_proposal : null;
  if (model) rememberModelProposal(query, model);
  return model;
}

/**
 * 결정례 힌트만 따로(2026-09-29) — 긴 서술형은 결정례 힌트가 3~60초 걸려(측정) 첫 계산에서 떼어 냈다.
 * 서버는 기존 엔진·결정트리를 돌리지 않고 힌트만 준다(30분 캐시). 실패는 null(fail-open).
 */
export async function fetchPrecedentHints({ query, hintQueries = [], token, signal, fetchImpl = fetch, withModel = false, productDetails = null, researchSummary = "" }) {
  if (!token || !query) return null;
  const item = { keyword: query };
  // 웹 조사 요약(2026-10-04) — 조사가 끝난 뒤 PROVE 모델을 한 번 더 묻는다(150개 실측 호 40.7→51.3%)
  if (withModel && researchSummary) item.research_summary = String(researchSummary).slice(0, 4000);
  if (withModel && productDetails && typeof productDetails === "object") item.product_details = productDetails;
  const extra = (Array.isArray(hintQueries) ? hintQueries : []).map((h) => String(h || "").trim()).filter(Boolean).slice(0, 3);
  if (extra.length) item.precedent_hint_queries = extra;
  try {
    const response = await fetchImpl("/api/onto/classify/bulk", {
      method: "POST",
      headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
      // PROVE 학습 모델 제안(2026-09-29)은 같은 요청에 얹는다 — 서버가 모델과 힌트를 함께 불러 한 번에 준다.
      body: JSON.stringify({ items: [item], options: withModel ? { precedent_hints_only: true, model_proposal: true } : { precedent_hints_only: true } }),
      signal,
    });
    if (!response.ok) return null;
    const payload = await response.json();
    const row = Array.isArray(payload?.results) ? payload.results[0] : null;
    const hints = row && row.precedent_hints && typeof row.precedent_hints === "object" ? row.precedent_hints : null;
    const model = row && row.model_proposal && typeof row.model_proposal === "object" ? row.model_proposal : null;
    // 모델 제안은 힌트 객체에 실어 보낸다 — 카드·지도·표가 결정례 힌트를 넘기는 길을 그대로 탄다.
    if (model) {
      rememberModelProposal(query, model);
      return { ...(hints || {}), model_proposal: model };
    }
    return hints;
  } catch (_) {
    return null;
  }
}

/**
 * 필수·배제 카드(2026-09-25) — 고른 세번이 되려면 맞아야 하는 조건과, 해당하면 다른 곳으로 가는 조건.
 * 서버는 검수 트리의 조건식·국내 결정표만 읽고 답한 사실로 상태를 매긴다(판정·저장 아님, final:false).
 */
export async function fetchTargetConditions({ code, facts = {}, token, signal, fetchImpl = fetch }) {
  const digits = String(code || "").replace(/\D/g, "");
  if (!token || digits.length < 4) return null;
  const response = await fetchImpl("/api/onto/classify/preview/target-conditions", {
    method: "POST",
    headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
    body: JSON.stringify({ code: digits, facts: facts || {} }),
    signal,
  });
  if (!response.ok) throw new Error(`HTTP ${response.status}`);
  return response.json();
}
